import { useTranslation } from '@helios/frontend-core';
import React from 'react';
import { useViewport } from '../../hooks/useViewport';
import { SunnyChat } from '../SunnyChat/SunnyChat';
import { EXPANDED_WIDTH } from '../SunnyChat/constants';
import { UseSunnyChatResult } from '../SunnyChat/useSunnyChat';
import { ARTBOARD_WIDTH_PX, RiveBottomNav, RiveBottomNavArtboard } from './RiveBottomNav';

export interface CollapsibleRiveBottomNavProps {
  sunnyChat: UseSunnyChatResult;
  isCollapsed: boolean;
  artboard: RiveBottomNavArtboard;
  activeItemId: string;
  onNavClick: (id: string) => void;
  onCollapsedNavClick: () => void;
  onTabSettled?: (id: string) => void;
}

const EXPANDED_CARD_MARGIN_PX = 16;
const COLLAPSED_NAV_GAP_PX = 10;
const COLLAPSED_NAV_VISIBLE_ICON_RIGHT_EDGE_PX = 92;
const BAR_EXTRA_WIDTH_PX = 32;
const LAUNCHER_SIZE_PX = 54;
const NAV_ROW_BOTTOM_OFFSET = 'max(34px, env(safe-area-inset-bottom))';
const SUNNY_CHAT_BOTTOM_OFFSET = 'max(40px, env(safe-area-inset-bottom))';
const NAV_ROW_HEIGHT_PX = 60;
const NAV_CONTENT_MAX_WIDTH_PX = 295;
const NAV_CONTENT_MARGIN_CAP_PX = 40;
const ABOVE_NAV_GAP_PX = 8;
export const CollapsibleRiveBottomNav: React.FC<CollapsibleRiveBottomNavProps> = ({
  sunnyChat,
  isCollapsed,
  artboard,
  activeItemId,
  onNavClick,
  onCollapsedNavClick,
  onTabSettled,
}) => {
  const { t } = useTranslation();
  const { width: viewportWidth } = useViewport();
  const expandedMaxWidth = Math.min(viewportWidth - EXPANDED_CARD_MARGIN_PX * 2, EXPANDED_WIDTH);
  const navHorizontalPadding = Math.max(
    0,
    Math.min(NAV_CONTENT_MARGIN_CAP_PX, (viewportWidth - NAV_CONTENT_MAX_WIDTH_PX) / 2),
  );
  const navContentWidth = viewportWidth - navHorizontalPadding * 2;
  const navPillWidth = Math.min(ARTBOARD_WIDTH_PX, navContentWidth);
  const navPillHorizontalOffset = navHorizontalPadding + (navContentWidth - navPillWidth) / 2;
  const aboveBottomOffset = `calc(${NAV_ROW_HEIGHT_PX + ABOVE_NAV_GAP_PX}px + ${NAV_ROW_BOTTOM_OFFSET})`;
  const inlineBarBottomOffset = `calc(6px + ${NAV_ROW_BOTTOM_OFFSET})`;

  const collapsedNavLeft = EXPANDED_CARD_MARGIN_PX;
  const collapsedNavRight = collapsedNavLeft + navPillWidth;
  const collapsedNavVisibleIconRight = Math.min(
    collapsedNavLeft + COLLAPSED_NAV_VISIBLE_ICON_RIGHT_EDGE_PX,
    collapsedNavRight,
  );
  const hasRoomBesideOpenNav =
    collapsedNavRight + COLLAPSED_NAV_GAP_PX + EXPANDED_WIDTH + EXPANDED_CARD_MARGIN_PX <= viewportWidth;
  const isNavVisuallyCollapsed = isCollapsed && !hasRoomBesideOpenNav;

  const barMaxWidth = Math.min(
    viewportWidth -
      collapsedNavVisibleIconRight -
      COLLAPSED_NAV_GAP_PX -
      EXPANDED_CARD_MARGIN_PX +
      BAR_EXTRA_WIDTH_PX,
    EXPANDED_WIDTH,
  );

  const isLauncherMode = sunnyChat.shouldRender && sunnyChat.mode === 'launcher';
  const launcherMinLeftForOwnWidth = viewportWidth - EXPANDED_CARD_MARGIN_PX - LAUNCHER_SIZE_PX;
  const launcherLeftClearance = Math.min(
    (isNavVisuallyCollapsed ? collapsedNavVisibleIconRight : collapsedNavRight) + COLLAPSED_NAV_GAP_PX,
    launcherMinLeftForOwnWidth,
  );
  const openLauncherNavRight =
    isLauncherMode && !isCollapsed
      ? Math.min(collapsedNavRight, launcherMinLeftForOwnWidth - COLLAPSED_NAV_GAP_PX)
      : collapsedNavRight;
  const positionTransition = 'transition-[left,right,bottom] duration-300 ease-out';

  return (
    <div className="relative flex flex-col items-end gap-8">
      {sunnyChat.shouldRender && (
        <div
          className={`pointer-events-none fixed z-40 flex items-end justify-end ${positionTransition}`}
          style={{
            left: isLauncherMode
              ? launcherLeftClearance
              : isCollapsed || sunnyChat.mode === 'expanded'
              ? EXPANDED_CARD_MARGIN_PX
              : navPillHorizontalOffset,
            right: isLauncherMode
              ? EXPANDED_CARD_MARGIN_PX
              : isCollapsed || sunnyChat.mode === 'expanded'
              ? EXPANDED_CARD_MARGIN_PX
              : navPillHorizontalOffset,
            bottom: isLauncherMode
              ? SUNNY_CHAT_BOTTOM_OFFSET
              : isCollapsed
              ? inlineBarBottomOffset
              : aboveBottomOffset,
          }}
        >
          <SunnyChat
            {...sunnyChat}
            trackingId="sunny-chat"
            fillBarWidth={sunnyChat.mode === 'bar'}
            maxWidth={sunnyChat.mode === 'expanded' ? expandedMaxWidth : isCollapsed ? barMaxWidth : navPillWidth}
            isInlineWithNav={isLauncherMode || isCollapsed}
            className="pointer-events-auto"
          />
        </div>
      )}

      <div
        key="nav-slot"
        className={`fixed z-30 ${positionTransition}`}
        style={{
          left: isCollapsed || isLauncherMode ? collapsedNavLeft : navHorizontalPadding,
          right: isCollapsed || isLauncherMode ? viewportWidth - openLauncherNavRight : navHorizontalPadding,
          bottom: NAV_ROW_BOTTOM_OFFSET,
        }}
      >
        <RiveBottomNav
          key={artboard}
          artboard={artboard}
          activeItemId={activeItemId}
          onNavClick={onNavClick}
          isCollapsed={isNavVisuallyCollapsed}
          collapseResyncToken={sunnyChat.mode}
          className="h-[60px] w-full"
          onTabSettled={onTabSettled}
        />
        {isNavVisuallyCollapsed && (
          <button
            type="button"
            aria-label={t('navigation.expand')}
            onClick={onCollapsedNavClick}
            className="absolute left-0 top-0 h-full"
            style={{ width: COLLAPSED_NAV_VISIBLE_ICON_RIGHT_EDGE_PX }}
          />
        )}
      </div>
    </div>
  );
};
